import React, { useMemo, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import type { Appointment } from "../../../types";
import {
  formatTime,
  formatWeekdayShortDate,
} from "../../../utils/dateUtils";
import {
  getAppointmentReminderWhatsAppMessage,
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";
import { Card, ClientCarCard, ErrorState, Icons, Loading } from "../../shared";

function groupAppointmentsByDate(
  appointments: Appointment[]
): { date: string; items: Appointment[] }[] {
  const byDate = new Map<string, Appointment[]>();

  for (const appointment of appointments) {
    const date = appointment.appointmentDate;
    const list = byDate.get(date) ?? [];
    list.push(appointment);
    byDate.set(date, list);
  }

  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({
      date,
      items: [...items].sort((a, b) => {
        const aMinimized = isAppointmentMinimized(a) ? 1 : 0;
        const bMinimized = isAppointmentMinimized(b) ? 1 : 0;
        if (aMinimized !== bMinimized) return aMinimized - bMinimized;
        return (a.startTime || "").localeCompare(b.startTime || "");
      }),
    }));
}

function getProfessionalName(appointment: Appointment): string | null {
  return (
    appointment.professional?.name ||
    appointment.professionalName ||
    null
  );
}

function isAppointmentCompleted(appointment: Appointment): boolean {
  return appointment.status?.toLowerCase() === "completed";
}

/** Already reminded or finished — show compact by default. */
function isAppointmentMinimized(appointment: Appointment): boolean {
  return (
    Boolean(appointment.confirmationSent) || isAppointmentCompleted(appointment)
  );
}

export interface WeekAppointmentRemindersPanelProps {
  appointments: Appointment[];
  loading: boolean;
  error: string | null;
  companyName: string;
  markingIds: Set<number>;
  onRetry?: () => void;
  onWhatsAppReminder: (appointment: Appointment) => void;
  onEditAppointment: (appointment: Appointment) => void;
  onCancelAppointment: (appointment: Appointment) => void;
}

export const WeekAppointmentRemindersPanel: React.FC<
  WeekAppointmentRemindersPanelProps
> = ({
  appointments,
  loading,
  error,
  companyName,
  markingIds,
  onRetry,
  onWhatsAppReminder,
  onEditAppointment,
  onCancelAppointment,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());

  const grouped = useMemo(
    () => groupAppointmentsByDate(appointments),
    [appointments]
  );

  const showEmpty = !loading && !error && appointments.length === 0;
  const remindedCount = appointments.filter((a) => a.confirmationSent).length;
  const completedCount = appointments.filter((a) =>
    isAppointmentCompleted(a)
  ).length;
  const pendingCount = appointments.filter(
    (a) => !isAppointmentMinimized(a)
  ).length;

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {!loading && !error && appointments.length > 0 && (
        <Card className="p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-slate-400">
            <span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {appointments.length}
              </span>{" "}
              {appointments.length === 1 ? "agendamento" : "agendamentos"} na
              semana
            </span>
            <span className="hidden sm:inline text-gray-300 dark:text-slate-600">
              ·
            </span>
            <span>
              <span className="font-semibold text-amber-700 dark:text-amber-400">
                {pendingCount}
              </span>{" "}
              pendentes
            </span>
            <span className="hidden sm:inline text-gray-300 dark:text-slate-600">
              ·
            </span>
            <span>
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                {remindedCount}
              </span>{" "}
              {remindedCount === 1 ? "lembrete enviado" : "lembretes enviados"}
            </span>
            {completedCount > 0 ? (
              <>
                <span className="hidden sm:inline text-gray-300 dark:text-slate-600">
                  ·
                </span>
                <span>
                  <span className="font-semibold text-sky-700 dark:text-sky-400">
                    {completedCount}
                  </span>{" "}
                  {completedCount === 1 ? "concluído" : "concluídos"}
                </span>
              </>
            ) : null}
          </div>
        </Card>
      )}

      {loading && (
        <Card className="p-6 border border-gray-100 shadow-sm">
          <Loading
            message="Carregando agendamentos da semana…"
            size="md"
            className="py-8"
          />
        </Card>
      )}

      {!loading && error && (
        <Card className="p-6 border border-gray-100 shadow-sm">
          <ErrorState
            message={error}
            variant="inline"
            onRetry={onRetry}
            className="py-4"
          />
        </Card>
      )}

      {showEmpty && (
        <Card className="p-10 sm:p-14 border border-gray-100 shadow-sm">
          <div className="flex flex-col items-center text-center max-w-md mx-auto">
            <Icons.Event sx={{ fontSize: 56 }} className="text-sky-400" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
              Nenhum agendamento nesta semana
            </h2>
            <p className="mt-2 text-gray-600 dark:text-slate-400 text-sm sm:text-base leading-relaxed">
              Não há agendamentos ativos para lembrar no período selecionado.
            </p>
          </div>
        </Card>
      )}

      {!loading &&
        !error &&
        grouped.map(({ date, items }) => (
          <Card
            key={date}
            className="p-4 sm:p-6 border border-gray-100 shadow-sm"
          >
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white capitalize">
              {formatWeekdayShortDate(date)}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
              {items.length}{" "}
              {items.length === 1 ? "agendamento" : "agendamentos"}
            </p>

            <ul className="mt-4 space-y-3">
              {items.map((appointment) => {
                const { client, clientCar, service } = appointment;
                const hasPhone = Boolean(client.phoneNumber?.trim());
                const professionalName = getProfessionalName(appointment);
                const isMarking = markingIds.has(appointment.id);
                const reminded = Boolean(appointment.confirmationSent);
                const completed = isAppointmentCompleted(appointment);
                const minimized = isAppointmentMinimized(appointment);
                const isExpanded = expandedIds.has(appointment.id);
                const showFull = !minimized || isExpanded;

                const statusBadge = completed ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-800">
                    <Icons.Check sx={{ fontSize: 14 }} />
                    Concluído
                  </span>
                ) : reminded ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    <Icons.Check sx={{ fontSize: 14 }} />
                    Lembrete enviado
                  </span>
                ) : null;

                const reminderActions = (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onEditAppointment(appointment)}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/40 border border-sky-200 dark:border-sky-800 transition-colors"
                      title="Editar data do agendamento"
                      aria-label={`Editar agendamento de ${client.name}`}
                    >
                      <Icons.Edit sx={{ fontSize: 14 }} />
                    </button>

                    {!completed ? (
                      <button
                        type="button"
                        onClick={() => onCancelAppointment(appointment)}
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 border border-red-200 dark:border-red-800 transition-colors"
                        title="Cancelar agendamento"
                        aria-label={`Cancelar agendamento de ${client.name}`}
                      >
                        <Icons.Delete sx={{ fontSize: 14 }} />
                      </button>
                    ) : null}

                    {hasPhone && !completed ? (
                      <button
                        type="button"
                        disabled={isMarking}
                        onClick={() => {
                          sendWhatsAppMessage({
                            phoneNumber: client.phoneNumber,
                            message: getAppointmentReminderWhatsAppMessage({
                              clientName: client.name,
                              companyName,
                              appointmentDate: appointment.appointmentDate,
                              startTime: appointment.startTime,
                              serviceName: service.name,
                              carModel: clientCar?.model,
                              carPlate: clientCar?.plate,
                            }),
                          });
                          onWhatsAppReminder(appointment);
                        }}
                        className={`${getWhatsAppButtonStyles("sm")} ${
                          isMarking ? "opacity-60 cursor-wait" : ""
                        }`}
                        title={
                          reminded
                            ? "Reenviar lembrete no WhatsApp"
                            : "Enviar lembrete no WhatsApp"
                        }
                        aria-label={`Enviar lembrete por WhatsApp para ${client.name}`}
                      >
                        <svg
                          className={getWhatsAppIconStyles("sm")}
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path d={WHATSAPP_ICON_PATH} />
                        </svg>
                      </button>
                    ) : !hasPhone && !completed ? (
                      <span className="text-xs text-gray-400 italic">
                        Sem telefone
                      </span>
                    ) : null}
                  </div>
                );

                const reminderMeta = [
                  appointment.startTime
                    ? formatTime(appointment.startTime)
                    : null,
                  professionalName,
                ]
                  .filter(Boolean)
                  .join(" · ");

                if (minimized && !showFull) {
                  return (
                    <li key={appointment.id}>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(appointment.id)}
                        className="w-full flex items-center gap-3 rounded-xl border border-gray-100 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/40 px-3 py-2.5 text-left hover:bg-gray-100/80 dark:hover:bg-slate-800/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                        aria-expanded={false}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-medium text-gray-800 dark:text-slate-200 truncate">
                              {client.name}
                            </p>
                            {statusBadge}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate">
                            {[
                              appointment.startTime
                                ? formatTime(appointment.startTime)
                                : null,
                              service.name,
                              clientCar
                                ? `${clientCar.model} · ${clientCar.plate}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                        <ExpandMore
                          sx={{
                            fontSize: 22,
                            color: "#9ca3af",
                            flexShrink: 0,
                            transform: "rotate(0deg)",
                          }}
                          aria-hidden
                        />
                      </button>
                    </li>
                  );
                }

                return (
                  <li key={appointment.id}>
                    {minimized ? (
                      <div className="mb-2">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(appointment.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                          aria-expanded
                        >
                          <ExpandMore
                            sx={{
                              fontSize: 18,
                              transform: "rotate(180deg)",
                            }}
                          />
                          Recolher
                        </button>
                      </div>
                    ) : null}

                    {clientCar ? (
                      <ClientCarCard
                        car={clientCar}
                        size="md"
                        clientName={client.name}
                        serviceName={service.name}
                        message={statusBadge}
                        meta={reminderMeta || undefined}
                        actions={reminderActions}
                      />
                    ) : (
                      <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-3 sm:px-4">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {client.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                            {service.name}
                          </p>
                          {reminderMeta ? (
                            <p className="text-sm text-gray-500 dark:text-slate-400 mt-1.5">
                              {reminderMeta}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">{statusBadge}</div>
                          <div className="shrink-0">{reminderActions}</div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        ))}
    </div>
  );
};
